import { useCallback } from "react";
import useSWR from "swr";
import useSWRMutation from "swr/mutation";
import { PaginationQuery } from "../../types";
import { buildUrl, mutateCache, useApi } from "../api";
import { CustomError } from "../errors";
import { InsertTimberBody, UpdateTimberBody } from "./types/timber";
import { SearchTimbersResponse, TimberClass } from "./types/timberClass";

const brTimbersUrl = "/admin/modules/beam_reinforcement/timbers";

export const useBRTimbersApi = () => {
  const api = useApi();

  const useBRTimbers = useCallback(
    (query: PaginationQuery, signal: AbortSignal) => {
      const url = buildUrl(brTimbersUrl, query);
      return useSWR<SearchTimbersResponse, CustomError, string>(url, (requestUrl) =>
        api.get<SearchTimbersResponse>({ url: requestUrl, signal }),
      );
    },
    [api],
  );

  const getBRTimber = useCallback(
    (name: string, signal: AbortSignal) => {
      return useSWRMutation<TimberClass, CustomError, string>(`${brTimbersUrl}/${encodeURIComponent(name)}`, (url) =>
        api.get<TimberClass>({ url, signal }),
      );
    },
    [api],
  );

  const insertBRTimber = useCallback(
    async (body: InsertTimberBody): Promise<{ id: string }> => {
      const result = await api.post<{ id: string }, InsertTimberBody>(brTimbersUrl, body);
      mutateCache([brTimbersUrl]);
      return result;
    },
    [api],
  );

  const updateBRTimber = useCallback(
    async (sourceName: string, body: UpdateTimberBody): Promise<void> => {
      const url = `${brTimbersUrl}/${encodeURIComponent(sourceName)}`;
      await api.put<UpdateTimberBody>(url, body);
      mutateCache([brTimbersUrl], url);
    },
    [api],
  );

  const deleteBRTimber = useCallback(
    async (name: string): Promise<void> => {
      const url = `${brTimbersUrl}/${encodeURIComponent(name)}`;
      await api.del(url);
      mutateCache([brTimbersUrl], url);
    },
    [api],
  );

  const updateBRTimberVisibility = useCallback(
    async (name: string, body: { visibility: string }): Promise<void> => {
      const url = `${brTimbersUrl}/${encodeURIComponent(name)}/visibility`;
      await api.put<{ visibility: string }>(url, body);
      mutateCache([brTimbersUrl], url);
    },
    [api],
  );

  const updateBRTimberOrder = useCallback(
    async (name: string, body: { order: number }): Promise<void> => {
      const url = `${brTimbersUrl}/${encodeURIComponent(name)}/order`;
      await api.put<{ order: number }>(url, body);
      mutateCache([brTimbersUrl], url);
    },
    [api],
  );

  return {
    useBRTimbers,
    getBRTimber,
    insertBRTimber,
    updateBRTimber,
    deleteBRTimber,
    updateBRTimberVisibility,
    updateBRTimberOrder,
  };
};

export default useBRTimbersApi;
